import React, { useEffect, useRef, useState } from 'react';
import {
  LogOut,
  Coins,
  LayoutGrid,
  ChevronDown,
  Sparkles,
  Layers3,
  Sofa,
  PenTool,
  Globe,
  MapPinned,
  Film,
  Monitor,
  Wrench,
  LayoutDashboard,
  MoonStar,
  Scissors,
  Video,
  Clapperboard,
  SlidersHorizontal,
  Receipt,
  Loader2,
  CircleDot,
  Waves,
  FileText,
  FilePen,
} from 'lucide-react';
import { supabase, openBillingPortal } from '../services/supabase';

interface HeaderProps {
  userEmail?: string | null;
  userCredits?: number;
  onSignOut?: () => void;
  onBuyCredits?: () => void;
  onLogin?: () => void;
  onSignUp?: () => void;
}

// Apps grouped the way the Hub buckets them, so the switcher gives context.
// Each group carries an accent colour + gradient used to theme its section.
const APP_GROUPS = [
  {
    id: 'workspace', label: 'Workspace',
    accent: 'text-purple-300', iconBg: 'bg-purple-500/15 text-purple-300', gradient: 'from-purple-500/20',
    apps: [
      { id: 'hub', label: 'Hub', icon: LayoutDashboard, url: 'https://hub.rehub.studio' },
      { id: 'flow', label: 'Flow', icon: Waves, url: 'https://flow.rehub.studio' },
      { id: 'studio', label: 'Studio', icon: Clapperboard, url: 'https://studio.rehub.studio' },
      { id: 'assistant', label: 'Assistant', icon: Sparkles, url: 'https://assistant.rehub.studio/assistant?sourceApp=hyperdusk' },
    ],
  },
  {
    id: 'photo', label: 'AI Photo',
    accent: 'text-sky-300', iconBg: 'bg-sky-500/15 text-sky-300', gradient: 'from-sky-500/20',
    apps: [
      { id: 'photoclear', label: 'Photoclear', icon: Sparkles, url: 'https://photoclear.rehub.studio' },
      { id: 'hdrphoto', label: 'HDR Photo', icon: Layers3, url: 'https://hdrphoto.rehub.studio' },
      { id: 'photoedit', label: 'PhotoEdit', icon: SlidersHorizontal, url: 'https://photoedit.rehub.studio' },
      { id: 'staging', label: 'Staging', icon: Sofa, url: 'https://staging.rehub.studio' },
    ],
  },
  {
    id: 'video', label: 'AI Video',
    accent: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 text-fuchsia-300', gradient: 'from-fuchsia-500/20',
    apps: [
      { id: 'imagemotion', label: 'Imagemotion', icon: Film, url: 'https://imagemotion.rehub.studio' },
      { id: 'videomotion', label: 'Videomotion', icon: Video, url: 'https://videomotion.rehub.studio' },
      { id: 'propertycut', label: 'PropertyCuts', icon: Scissors, url: 'https://propertycuts.rehub.studio' },
      { id: 'hyperdusk', label: 'Hyper Dusk', icon: MoonStar, url: 'https://hyperdusk.rehub.studio' },
      { id: 'record', label: 'Screen Record', icon: CircleDot, url: 'https://record.rehub.studio' },
    ],
  },
  {
    id: 'tours', label: 'Tours & Floor Plans',
    accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15 text-emerald-300', gradient: 'from-emerald-500/20',
    apps: [
      { id: 'tours', label: '360 Tours', icon: Globe, url: 'https://360tours.rehub.studio' },
      { id: 'planos', label: 'Planos', icon: MapPinned, url: 'https://planos.rehub.studio' },
      { id: 'sketchplan', label: 'Sketchplan', icon: PenTool, url: 'https://sketchplan.rehub.studio' },
    ],
  },
  {
    id: 'present', label: 'Presentations',
    accent: 'text-amber-300', iconBg: 'bg-amber-500/15 text-amber-300', gradient: 'from-amber-500/20',
    apps: [
      { id: 'showcase', label: 'Showcase', icon: Monitor, url: 'https://showcase.rehub.studio' },
      { id: 'brochures', label: 'BrochurePDF', icon: FileText, url: 'https://brochures.rehub.studio' },
      { id: 'pdf', label: 'ReHub PDF', icon: FilePen, url: 'https://pdf.rehub.studio' },
    ],
  },
  {
    id: 'tools', label: 'Free Tools',
    accent: 'text-slate-300', iconBg: 'bg-slate-500/15 text-slate-300', gradient: 'from-slate-500/20',
    apps: [
      { id: 'tools', label: 'Tools', icon: Wrench, url: 'https://tools.rehub.studio' },
    ],
  },
] as const;

const APPS = APP_GROUPS.flatMap((g) => g.apps as readonly (typeof APP_GROUPS)[number]['apps'][number][]);

type AppId = typeof APP_GROUPS[number]['apps'][number]['id'];

const handleAppNavigation = async (targetApp: AppId, url: string, currentApp: AppId, e: React.MouseEvent) => {
  if (targetApp === currentApp) {
    e.preventDefault();
    return;
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      e.preventDefault();
      window.location.href = `${url}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`;
    }
  } catch {
    // Fall back to default link navigation.
  }
};

const ProductSwitcher: React.FC<{ currentApp: AppId }> = ({ currentApp }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <LayoutGrid size={16} />
        <span>Apps</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl z-50">
          <div className="relative overflow-hidden rounded-2xl">
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(120%_80%_at_100%_100%,rgba(56,189,248,0.12),transparent_55%)]" />
            <div className="relative p-3 space-y-3">
              {APP_GROUPS.map((group) => (
                <div key={group.id}>
                  <div className={`px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider ${group.accent}`}>{group.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.apps.map((app) => (
                      <a
                        key={app.id}
                        href={app.url}
                        onClick={(e) => { handleAppNavigation(app.id, app.url, currentApp, e); setOpen(false); }}
                        className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          currentApp === app.id
                            ? `bg-gradient-to-br ${group.gradient} to-transparent text-white ring-1 ring-inset ring-white/15`
                            : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                        }`}
                      >
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${group.iconBg}`}>
                          <app.icon size={16} strokeWidth={1.8} />
                        </span>
                        <span className="truncate">{app.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileAppSwitcher: React.FC<{ currentApp: AppId }> = ({ currentApp }) => (
  <div className="flex items-center gap-1.5" aria-label="Switch app">
    {APPS.map(app => (
      <a
        key={app.id}
        href={app.url}
        onClick={(e) => handleAppNavigation(app.id, app.url, currentApp, e)}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all touch-manipulation ${
          currentApp === app.id
            ? 'bg-orange-600 border-orange-500 text-white'
            : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 active:bg-slate-800'
        }`}
        aria-label={`Switch to ${app.label}`}
      >
        <app.icon size={18} strokeWidth={2} />
      </a>
    ))}
  </div>
);

const Header: React.FC<HeaderProps> = ({
  userEmail,
  userCredits,
  onSignOut,
  onBuyCredits,
  onLogin,
  onSignUp,
}) => {
  const logoSrc = '/hyperdusk-logo-wordmark-alpha.png';
  const isPublicHeader = !userEmail;
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <header className={`sticky top-0 left-0 right-0 z-40 px-4 sm:px-6 flex flex-col md:flex-row md:items-center ${
      isPublicHeader
        ? 'bg-black/25 backdrop-blur-md border-b border-white/10'
        : 'bg-black border-b border-slate-900'
    }`}>
      <div className="w-full min-h-16 flex items-center justify-between gap-3 py-3 md:py-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <img
            src={logoSrc}
            alt="Hyper Dusk"
            className={`h-8 md:h-9 w-auto object-contain ${isPublicHeader ? 'mix-blend-screen brightness-110 contrast-110' : ''}`}
          />
          <ProductSwitcher currentApp="hyperdusk" />
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {userEmail ? (
            <>
              <span className="text-sm text-slate-400 hidden sm:block">{userEmail}</span>
              {userCredits !== undefined && (
                userCredits === 0 ? (
                  <button
                    onClick={onBuyCredits}
                    className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 rounded-full border-2 border-orange-400 transition-all shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 animate-pulse hover:animate-none touch-manipulation min-h-[40px]"
                  >
                    <div className="w-6 h-6 rounded-full bg-red-700 text-white flex items-center justify-center border border-red-400 shrink-0">
                      <Coins size={12} />
                    </div>
                    <div className="flex flex-col items-start leading-none text-left">
                      <span className="font-bold text-white text-xs">Top Up</span>
                      <span className="text-[10px] text-orange-100 font-medium hidden sm:block">0 left</span>
                    </div>
                  </button>
                ) : (
                  <button
                    onClick={onBuyCredits}
                    className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-orange-600 hover:bg-orange-500 rounded-full border-2 border-orange-400 transition-all shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 touch-manipulation min-h-[40px]"
                  >
                    <div className="w-6 h-6 rounded-full bg-orange-700 text-white flex items-center justify-center border border-orange-400 shrink-0">
                      <LayoutDashboard size={12} />
                    </div>
                    <div className="flex flex-col items-start leading-none">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-white text-sm sm:text-base">{userCredits}</span>
                        <Coins size={12} className="text-white" />
                      </div>
                      <span className="text-[10px] text-orange-100 font-semibold hidden sm:block">Credits</span>
                    </div>
                  </button>
                )
              )}
              {onSignOut && (
                <div className="relative">
                  <button
                    onClick={handleBillingPortal}
                    disabled={billingLoading}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-800 rounded hover:bg-slate-700 transition-colors disabled:opacity-60"
                  >
                    {billingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                    <span className="hidden sm:inline">Billing &amp; invoices</span>
                  </button>
                  {billingNotice && (
                    <div className="absolute right-0 top-full mt-1 w-64 px-3 py-2 text-xs text-amber-300/90 bg-slate-900 border border-slate-700 rounded-lg shadow-lg z-50">
                      {billingNotice}
                    </div>
                  )}
                </div>
              )}
              {onSignOut && (
                <button
                  onClick={onSignOut}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-800 rounded hover:bg-slate-700 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              )}
            </>
          ) : (
            <>
              {onLogin && (
                <button
                  onClick={onLogin}
                  className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium"
                >
                  Log In
                </button>
              )}
              {onSignUp && (
                <button
                  onClick={onSignUp}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-400 hover:to-pink-400 text-white rounded-xl text-sm font-semibold transition-all"
                >
                  Sign Up
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="md:hidden border-t border-slate-800 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
        <MobileAppSwitcher currentApp="hyperdusk" />
      </div>
    </header>
  );
};

export default Header;
